import { SEOHead } from "@/components/seo/SEOHead";
import { Link } from "@/lib/router-compat";
import { useRoOnlyGuard } from '@/hooks/useRoOnlyGuard';

export default function TermeniConditii() {
  const __roBlocked = useRoOnlyGuard();
  return (
    <>
      <SEOHead
        title="Termeni și Condiții - ROAutoMarket"
        description="Citește regulile pentru conturi, anunțuri și plăți pe RO Auto Market, precum și responsabilitățile cumpărătorilor și vânzătorilor."
        canonicalPath="/termeni-si-conditii"
      />
      <div className="container mx-auto px-4 py-10 max-w-4xl">
        <div className="surface-dark band-bleed -mt-10 mb-8 py-6"><h1 className="text-3xl md:text-4xl font-bold">Termeni și Condiții</h1></div>
        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6">
          <p className="text-sm text-muted-foreground">Ultima actualizare: 25.09.2026</p>
          <p>Operator: Vladimir Tudor, comerciant individual înregistrat în Regatul Unit, UTR 9810367572. Adresă de corespondență: 62 Clarkson Court, Hatfield, AL10 9GW, United Kingdom. Contact: <a href="mailto:contact@roautomarket.ro">contact@roautomarket.ro</a>. <Link to="/mentiuni-legale">Mențiuni legale</Link>.</p>

          <section>
            <h2 className="text-2xl font-semibold mb-3">1. Despre platformă</h2>
            <p>
              roautomarket.ro este o platformă marketplace care permite dealerilor și persoanelor fizice să listeze vehicule
              spre vânzare, iar cumpărătorilor să le caute și să contacteze direct vânzătorul. Platforma are rol de intermediar
              și nu este parte în tranzacțiile dintre utilizatori.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. Cont de utilizator</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Dealerii aleg un abonament Start, Standard sau Large. Persoanele fizice pot publica fără cont, după aprobarea anunțului.</li>
              <li>Pentru persoane fizice, publicarea unui anunț este plătită: pachetele Standard, Promovat sau Activ până la vânzare. Ultimul se reînnoiește lunar și poate fi anulat fără rambursarea perioadei achitate. Vezi <Link to="/pricing">prețurile și condițiile fiecărui pachet</Link>.</li>
              <li>Utilizatorul este responsabil pentru păstrarea în siguranță a credențialelor.</li>
              <li>Datele furnizate la înregistrare trebuie să fie reale și actualizate.</li>
              <li>Un utilizator poate avea un singur cont activ.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. Reguli pentru anunțuri</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Anunțurile trebuie să descrie corect vehiculul (marcă, model, an, km, dotări, defecte).</li>
              <li>Fotografiile trebuie să fie reale, recente și să aparțină vehiculului listat.</li>
              <li>Prețul afișat trebuie să fie cel real de vânzare.</li>
              <li>Nu sunt permise anunțuri duplicate pentru același vehicul.</li>
              <li>Vehiculele trebuie să fie disponibile legal pentru vânzare.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. Conținut și acțiuni interzise</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Informații false sau înșelătoare;</li>
              <li>Vehicule furate sau cu acte falsificate;</li>
              <li>Conținut ilegal, ofensator, discriminatoriu sau pornografic;</li>
              <li>Spam, scraping automat, atacuri asupra infrastructurii;</li>
              <li>Utilizarea datelor altor utilizatori în scopuri neautorizate;</li>
              <li>Crearea de conturi multiple pentru a eluda restricții.</li>
            </ul>
            <p>Încălcarea acestor reguli poate duce la suspendarea sau ștergerea contului, fără rambursare.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. Abonamente și plăți</h2>
            <p>
              Plățile sunt procesate de Stripe. Abonamentele dealer și pachetul Activ până la vânzare se reînnoiesc lunar și pot fi anulate. Pachetele Standard și Promovat pentru persoane fizice sunt valabile 30 de zile. Drepturile de retragere aplicabile consumatorilor rămân cele prevăzute de lege. Începerea imediată a serviciului și eventuala pierdere a dreptului de retragere necesită acordul expres al consumatorului.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. Limitarea răspunderii</h2>
            <p>
              roautomarket.ro nu este responsabilă pentru: acuratețea informațiilor introduse de utilizatori, calitatea sau
              starea reală a vehiculelor, neînțelegerile dintre vânzător și cumpărător, eventuale fraude între părți sau
              întreruperi temporare ale serviciului din motive tehnice. Cumpărătorul este responsabil să verifice personal
              vehiculul și documentele înainte de achiziție.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">7. Legea aplicabilă și soluționarea litigiilor</h2>
            <p>
              Operatorul este stabilit în Regatul Unit. Litigiile se soluționează pe cale amiabilă sau potrivit normelor legale aplicabile. Drepturile obligatorii ale consumatorilor nu sunt limitate de acești termeni.
            </p>
            <p>
              Consumatorii din România se pot adresa <a href="https://anpc.ro" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ANPC</a> și entităților de soluționare alternativă a litigiilor (SAL).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Insigna Dealer Verificat</h2>
            <p>Verificăm manual firma dealerului la ANAF și în RISCO: CUI, cod CAEN și statut fiscal. Nu verificăm starea vehiculelor. Insigna poate fi retrasă dacă datele firmei nu mai corespund.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Anunț creat din video cu AI</h2>
            <p>Videoclipul este folosit pentru extragerea fotografiilor și a datelor vehiculului. Utilizatorul verifică și confirmă informațiile înainte de trimiterea anunțului spre aprobare. AI nu publică singur anunțul.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">8. Modificări</h2>
            <p>Putem actualiza acești termeni. Vom afișa data ultimei modificări în partea de sus a paginii.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">9. Contact</h2>
            <p>Pentru întrebări: <a href="mailto:contact@roautomarket.ro" className="text-primary hover:underline">contact@roautomarket.ro</a></p>
          </section>
        </div>
      </div>
    </>
  );
}
